import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faWrench, faClock, faCheckCircle, faExclamationTriangle, 
    faInfoCircle, faCalendarAlt, faServer, faDatabase
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import localForage from 'localforage';

interface MaintenanceWindow {
    id: string;
    title: string;
    description: string;
    type: 'scheduled' | 'emergency' | 'completed';
    status: 'upcoming' | 'in-progress' | 'completed' | 'cancelled';
    startTime: string;
    endTime: string;
    affectedServices: string[];
    impact: 'low' | 'medium' | 'high';
    notifications: string[];
}

interface SystemStatus {
    service: string;
    status: 'operational' | 'degraded' | 'outage';
    uptime: string;
    lastIncident?: string;
}

const Maintenance: React.FC = () => {
    const [maintenanceWindows, setMaintenanceWindows] = useState<MaintenanceWindow[]>([]);
    const [systemStatus, setSystemStatus] = useState<SystemStatus[]>([]);
    const [clinicName, setClinicName] = useState<string>('Facility');

    useEffect(() => {
        document.title = "System Maintenance - Uzima Healthcare";
        loadClinicData();
        loadMaintenanceData();
        loadSystemStatus();
    }, []);

    const loadClinicData = async () => {
        try {
            const storedClinicName = await localForage.getItem('clinic_name');
            if (storedClinicName) {
                setClinicName(storedClinicName as string);
            }
        } catch (error) {
            console.error('Error loading clinic data:', error);
        }
    };

    const loadMaintenanceData = () => {
        // Mock maintenance data - in real app, fetch from API
        const mockMaintenance: MaintenanceWindow[] = [
            {
                id: 'MAINT-001',
                title: 'Database Optimization',
                description: 'Scheduled database optimization to improve query performance and reduce response times.',
                type: 'scheduled',
                status: 'upcoming',
                startTime: '2025-09-10T02:00:00Z',
                endTime: '2025-09-10T04:00:00Z',
                affectedServices: ['Database', 'API', 'User Management'],
                impact: 'medium',
                notifications: ['All users notified via email', 'In-app notification sent']
            },
            {
                id: 'MAINT-002',
                title: 'Security Patch Update',
                description: 'Critical security patches will be applied to all servers and applications.',
                type: 'scheduled',
                status: 'upcoming',
                startTime: '2025-09-15T01:00:00Z',
                endTime: '2025-09-15T03:00:00Z',
                affectedServices: ['All Services'],
                impact: 'high',
                notifications: ['Emergency maintenance notice sent']
            },
            {
                id: 'MAINT-003',
                title: 'CDN Server Maintenance',
                description: 'Content Delivery Network servers will be updated for improved global performance.',
                type: 'scheduled',
                status: 'completed',
                startTime: '2025-09-01T03:00:00Z',
                endTime: '2025-09-01T05:00:00Z',
                affectedServices: ['File Storage', 'Media Streaming'],
                impact: 'low',
                notifications: ['Maintenance completed successfully']
            },
            {
                id: 'MAINT-004',
                title: 'Emergency Database Recovery',
                description: 'Emergency maintenance to recover from database corruption incident.',
                type: 'emergency',
                status: 'completed',
                startTime: '2025-08-28T14:30:00Z',
                endTime: '2025-08-28T16:45:00Z',
                affectedServices: ['Database', 'All Applications'],
                impact: 'high',
                notifications: ['Emergency notice sent', 'Service restored']
            }
        ];
        setMaintenanceWindows(mockMaintenance);
    };

    const loadSystemStatus = () => {
        // Mock system status data - in real app, fetch from API
        const mockSystemStatus: SystemStatus[] = [
            {
                service: 'API Services',
                status: 'operational',
                uptime: '99.9%',
                lastIncident: '2025-08-15'
            },
            {
                service: 'Database',
                status: 'operational',
                uptime: '99.8%',
                lastIncident: '2025-08-28'
            },
            {
                service: 'Telehealth Platform',
                status: 'operational',
                uptime: '99.7%',
                lastIncident: '2025-08-20'
            },
            {
                service: 'File Storage',
                status: 'degraded',
                uptime: '98.5%',
                lastIncident: '2025-09-01'
            },
            {
                service: 'Email Services',
                status: 'operational',
                uptime: '99.6%',
                lastIncident: '2025-08-10'
            },
            {
                service: 'Billing System',
                status: 'operational',
                uptime: '99.9%',
                lastIncident: '2025-07-25'
            }
        ];
        setSystemStatus(mockSystemStatus);
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'operational': return faCheckCircle;
            case 'degraded': return faExclamationTriangle;
            case 'outage': return faExclamationTriangle;
            case 'upcoming': return faClock;
            case 'in-progress': return faWrench;
            case 'completed': return faCheckCircle;
            case 'cancelled': return faExclamationTriangle;
            default: return faInfoCircle;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'operational': return 'success';
            case 'degraded': return 'warning';
            case 'outage': return 'danger';
            case 'upcoming': return 'info';
            case 'in-progress': return 'warning';
            case 'completed': return 'success';
            case 'cancelled': return 'danger';
            default: return 'secondary';
        }
    };

    const getImpactColor = (impact: string) => {
        switch (impact) {
            case 'low': return 'success';
            case 'medium': return 'warning';
            case 'high': return 'danger';
            default: return 'secondary';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getUpcomingMaintenance = () => {
        return maintenanceWindows.filter(m => m.status === 'upcoming');
    };

    const getCompletedMaintenance = () => {
        return maintenanceWindows.filter(m => m.status === 'completed');
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <h2 className="fw-bold mb-1">
                                    <FontAwesomeIcon icon={faWrench} className="me-2 text-primary" />
                                    System Maintenance
                                </h2>
                                <p className="text-muted mb-0">
                                    Monitor system maintenance and status for {clinicName}
                                </p>
                            </div>
                        </div>

                        {/* System Status Overview */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white border-0">
                                        <h5 className="card-title mb-0">
                                            <FontAwesomeIcon icon={faServer} className="me-2" />
                                            System Status
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            {systemStatus.map((service, index) => (
                                                <div key={index} className="col-md-4 mb-3">
                                                    <div className="d-flex align-items-center">
                                                        <FontAwesomeIcon 
                                                            icon={getStatusIcon(service.status)} 
                                                            className={`text-${getStatusColor(service.status)} me-3`}
                                                        />
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-1">{service.service}</h6>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <span className={`badge bg-${getStatusColor(service.status)}`}>
                                                                    {service.status.toUpperCase()}
                                                                </span>
                                                                <small className="text-muted">
                                                                    Uptime: {service.uptime}
                                                                </small>
                                                            </div>
                                                            {service.lastIncident && (
                                                                <small className="text-muted">
                                                                    Last incident: {service.lastIncident}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Upcoming Maintenance */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white border-0">
                                        <h5 className="card-title mb-0">
                                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                                            Upcoming Maintenance
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {getUpcomingMaintenance().length > 0 ? (
                                            getUpcomingMaintenance().map((maintenance) => (
                                                <div key={maintenance.id} className="border-bottom pb-3 mb-3">
                                                    <div className="d-flex align-items-start justify-content-between">
                                                        <div className="flex-grow-1">
                                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                                <h6 className="mb-0">{maintenance.title}</h6>
                                                                <span className={`badge bg-${getStatusColor(maintenance.status)}`}>
                                                                    {maintenance.status.toUpperCase()}
                                                                </span>
                                                                <span className={`badge bg-${getImpactColor(maintenance.impact)}`}>
                                                                    {maintenance.impact.toUpperCase()} IMPACT
                                                                </span>
                                                            </div>
                                                            <p className="text-muted mb-2">{maintenance.description}</p>
                                                            <div className="d-flex align-items-center gap-4">
                                                                <small className="text-muted">
                                                                    <FontAwesomeIcon icon={faClock} className="me-1" />
                                                                    Start: {formatDate(maintenance.startTime)}
                                                                </small>
                                                                <small className="text-muted">
                                                                    End: {formatDate(maintenance.endTime)}
                                                                </small>
                                                            </div>
                                                            <div className="mt-2">
                                                                <small className="text-muted">Affected Services: </small>
                                                                {maintenance.affectedServices.map((service, index) => (
                                                                    <span key={index} className="badge bg-light text-dark me-1">
                                                                        {service}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center py-4">
                                                <FontAwesomeIcon icon={faCheckCircle} className="text-success fs-1 mb-3" />
                                                <h6 className="text-muted">No upcoming maintenance scheduled</h6>
                                                <p className="text-muted small">All systems are running smoothly</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Maintenance History */}
                        <div className="row">
                            <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white border-0">
                                        <h5 className="card-title mb-0">
                                            <FontAwesomeIcon icon={faDatabase} className="me-2" />
                                            Maintenance History
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {getCompletedMaintenance().map((maintenance) => (
                                            <div key={maintenance.id} className="border-bottom pb-3 mb-3">
                                                <div className="d-flex align-items-start justify-content-between">
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <h6 className="mb-0">{maintenance.title}</h6>
                                                            <span className={`badge bg-${getStatusColor(maintenance.status)}`}>
                                                                {maintenance.status.toUpperCase()}
                                                            </span>
                                                            <span className={`badge bg-${getImpactColor(maintenance.impact)}`}>
                                                                {maintenance.impact.toUpperCase()} IMPACT
                                                            </span>
                                                        </div>
                                                        <p className="text-muted mb-2">{maintenance.description}</p>
                                                        <div className="d-flex align-items-center gap-4">
                                                            <small className="text-muted">
                                                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                                                Completed: {formatDate(maintenance.endTime)}
                                                            </small>
                                                        </div>
                                                        <div className="mt-2">
                                                            <small className="text-muted">Affected Services: </small>
                                                            {maintenance.affectedServices.map((service, index) => (
                                                                <span key={index} className="badge bg-light text-dark me-1">
                                                                    {service}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <DashboardFooter />
        </>
    );
};

export default Maintenance;
